import { ArrowUpRight, TrendingUp, Users, Download, Radio } from "lucide-react";

const TOP_MARKETS = [
  { m: "United Kingdom", n: 28541 },
  { m: "United States", n: 22779 },
  { m: "Germany", n: 8514 },
  { m: "France", n: 7014 },
  { m: "Netherlands", n: 5632 },
];

const RECENT = [
  { title: "The CIO Playbook 2026: GenAI in Enterprise", tag: "Whitepaper", downloads: 2341 },
  { title: "Post-Cloud Cost Optimization Guide", tag: "Report", downloads: 1876 },
  { title: "Zero Trust Architecture — Live Panel", tag: "Webinar", downloads: 1412 },
  { title: "Building AI-native Data Platforms", tag: "eBook", downloads: 1204 },
];

const CAPTURE_MODES = [
  { n: "22,578", t: "Multi-Channel Capture", d: "Co-branded registration funnels capturing prospects at peak intent — webinars, events, whitepaper downloads, newsletters." },
  { n: "19,981", t: "Gated Premium Content", d: "On-demand webinars, buyer's guides, and technology explainers — all behind registration for verified lead capture." },
  { n: "5,859", t: "Engaged Tech Community", d: "Sustained engagement loop driving repeat visits & remarketing across niche UK tech topics." },
  { n: "8,931", t: "Event-Driven High-Intent", d: "Live and on-demand tech events that surface active buyers ready for sales outreach." },
];

export default function OwnedMedia() {
  return (
    <section id="owned-media" data-testid="owned-media-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Owned Media · Flagship Property
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em]">
            TechBytes<span className="text-teal">.</span>org.uk — <span className="italic font-normal">the UK tech lead engine.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            One owned publisher. One flagship property. First-party reach across the
            UK & EMEA tech buying community — awareness to decision, all captured.
          </p>
        </div>

        {/* Stats row */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
          {[
            { icon: TrendingUp, k: "Requests · 7d", v: "75,378", d: "↑ +18.4%" },
            { icon: Download, k: "Data served", v: "5.19 GB", d: "↑ sustained" },
            { icon: Users, k: "Subscribers", v: "3.1M", d: "↑ growing" },
            { icon: Radio, k: "Organic pieces / mo", v: "15–20", d: "editorial" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.k} className="bg-[#0A121A] p-6">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                  <Icon size={14} className="text-teal" /> {s.k}
                </div>
                <div className="font-display text-3xl md:text-4xl font-semibold">{s.v}</div>
                <div className="text-xs text-teal mt-1 font-mono">{s.d}</div>
              </div>
            );
          })}
        </div>

        {/* Flagship browser mockup */}
        <div className="mt-8 rounded-2xl border border-white/[0.06] overflow-hidden bg-[#040810]">
          <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <div className="ml-3 flex-1 rounded-md bg-black/40 px-3 py-1 text-[11px] font-mono text-muted-foreground">
              techbytes.org.uk/insights
            </div>
            <a
              href="https://techbytes.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-mono uppercase tracking-[0.15em] text-teal inline-flex items-center gap-1 hover:opacity-80"
            >
              Visit <ArrowUpRight size={12} />
            </a>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 md:p-10">
            <div className="lg:col-span-7">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-teal">
                Featured · Whitepaper
              </div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl font-semibold leading-tight max-w-lg">
                The CIO Playbook 2026: GenAI in the UK Enterprise
              </h3>
              <div className="mt-4 text-sm text-muted-foreground">2,341 downloads · gated</div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {RECENT.slice(1).map((r) => (
                  <div key={r.title} className="rounded-lg border border-white/5 p-4 card-hover">
                    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-teal mb-2">{r.tag}</div>
                    <div className="text-sm font-medium leading-snug">{r.title}</div>
                    <div className="mt-3 text-[11px] text-muted-foreground font-mono">{r.downloads.toLocaleString()} downloads</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-xl border border-white/5 p-5 glass">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">Top markets · last 7 days</div>
                <div className="space-y-3">
                  {TOP_MARKETS.map((m) => (
                    <div key={m.m}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>{m.m}</span>
                        <span className="font-mono text-teal text-xs">{m.n.toLocaleString()}</span>
                      </div>
                      <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-teal/60"
                          style={{ width: `${(m.n / 28541) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-teal/30 bg-teal/[0.04] p-5">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-teal mb-2">Subscribe · weekly</div>
                <div className="text-sm text-muted-foreground mb-3">Get the weekly Tech Intel briefing.</div>
                <div className="flex gap-2">
                  <input
                    placeholder="you@company.com"
                    className="flex-1 rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm outline-none focus:border-teal/50"
                    aria-label="Email"
                  />
                  <button
                    data-testid="techbytes-subscribe-btn"
                    className="rounded-md bg-teal text-[#050A0F] px-4 text-sm font-medium"
                  >
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Capture modes */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CAPTURE_MODES.map((c, i) => (
            <div key={c.t} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 card-hover">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display text-3xl font-semibold mt-2 mb-1">{c.n}</div>
              <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-teal mb-3">touchpoints</div>
              <div className="text-sm font-medium mb-2">{c.t}</div>
              <div className="text-xs text-muted-foreground leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
